import React, { useCallback, useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import s from './Profile.module.css';
import { getCroppedImage } from './cropUtils';
import IconAvatarTrash from '../../../../shared/ui/icons/IconAvatarTrash';
import IconProfileDelete from '../../../../shared/ui/icons/IconProfileDelete';

type Props = {
    src: string;
    isEditingExisting: boolean;
    busyExternal?: boolean;
    onCancel: () => void;
    onSave: (blob: Blob, dataUrlPreview: string) => void;
    onChangeImage: () => void;
    onConfirmDeleteExisting: () => Promise<void> | void;
};

const CropAvatarModal: React.FC<Props> = ({
    src,
    isEditingExisting,
    busyExternal = false,
    onCancel,
    onSave,
    onChangeImage,
    onConfirmDeleteExisting,
}) => {
    const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1.2);
    const [area, setArea] = useState<Area | null>(null);
    const [isSavingLocal, setIsSavingLocal] = useState(false);
    const [isDeletingLocal, setIsDeletingLocal] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const isBusy = busyExternal || isSavingLocal || isDeletingLocal;

    const onCropComplete = (_: Area, px: Area) => setArea(px);

    const handleSave = useCallback(async () => {
        if (!area || isBusy) return;
        setIsSavingLocal(true);
        try {
            const { blob, preview } = await getCroppedImage(src, area);
            onSave(blob, preview);
        } finally {
            setIsSavingLocal(false);
        }
    }, [area, src, onSave, isBusy]);

    const handleDeleteClick = () => {
        if (isBusy) return;
        if (isEditingExisting) setShowConfirm(true);
        else onCancel();
    };

    const confirmDelete = async () => {
        if (isBusy) return;
        setIsDeletingLocal(true);
        try {
            await onConfirmDeleteExisting();
        } finally {
            setIsDeletingLocal(false);
            setShowConfirm(false);
        }
    };

    return (
        <div
            className={s.modalBackdrop}
            role="dialog"
            aria-modal="true"
            aria-label="Edit profile image"
            aria-busy={isBusy}
        >
            <div className={s.modalCard}>
                <div className={s.modalHeader}>
                    <button
                        className={s.iconClose}
                        onClick={onCancel}
                        aria-label="Close"
                        disabled={isBusy}
                    >
                        ✕
                    </button>
                    <span className={s.modalTitle}>Edit profile image</span>
                    <button
                        className={s.iconTrash}
                        onClick={handleDeleteClick}
                        aria-label="Delete"
                        disabled={isBusy}
                    >
                        <IconAvatarTrash />
                    </button>
                </div>

                <div className={s.cropperWrap} aria-hidden={isBusy}>
                    <Cropper
                        image={src}
                        crop={crop}
                        zoom={zoom}
                        aspect={1}
                        cropShape="round"
                        showGrid={false}
                        objectFit="cover"
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onCropComplete={onCropComplete}
                    />
                </div>

                <div className={s.modalFooter}>
                    <button className={s.btnSecondary} onClick={onChangeImage} disabled={isBusy}>
                        Change
                    </button>
                    <button className={s.btnPrimary} onClick={handleSave} disabled={isBusy}>
                        {isSavingLocal || busyExternal ? 'Saving…' : 'Save'}
                    </button>
                </div>

                {isBusy && (
                    <div className={s.loadingOverlay} aria-live="polite">
                        <div className={s.spinner} />
                        <div className={s.loadingText}>
                            {isDeletingLocal ? 'Deleting…' : 'Saving…'}
                        </div>
                    </div>
                )}
            </div>

            {showConfirm && (
                <div
                    className={s.confirmBackdrop}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Remove avatar?"
                >
                    <div className={s.confirmCard}>
                        <div className={s.iconWrapper}>
                            <IconProfileDelete />
                        </div>
                        <div className={s.confirmTitle}>
                            Are you sure you want <br /> to delete this photo?
                        </div>
                        <div className={s.confirmText}>
                            This action will permanently remove the user's personal <br />{' '}
                            information. Once you delete this, it can’t be undone.
                        </div>
                        <div className={s.confirmActions}>
                            <button
                                className={s.btnSecondary}
                                onClick={() => setShowConfirm(false)}
                                disabled={isBusy}
                            >
                                Cancel
                            </button>
                            <button
                                className={s.btnDanger}
                                onClick={confirmDelete}
                                disabled={isBusy}
                            >
                                {isDeletingLocal || busyExternal ? 'Deleting…' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CropAvatarModal;
